/**
 * Pure decision helpers for the persistent recording dock.
 *
 * Recording is scoped to a single case. While a session is live the doctor may
 * move freely BETWEEN TABS of the owning case (the dock stays hidden there
 * because the in-panel Scribe view owns the surface). Leaving the owning case
 * — a different case, the case list, or any unrelated page — must be guarded
 * with a confirmation, and while off the scribe tab the floating dock is shown.
 */

const REPORT_STUDIO_CASE = /^\/report-studio\/([^/]+)(?:\/|$)/;

/** The case number a path belongs to, or null if it isn't a case path. */
export const caseNumberFromPath = (pathname: string): string | null => {
  const match = REPORT_STUDIO_CASE.exec(pathname);
  return match ? match[1] : null;
};

const isScribeTab = (pathname: string, caseNumber: string): boolean =>
  pathname === `/report-studio/${caseNumber}/assessment-scribe` ||
  pathname.startsWith(`/report-studio/${caseNumber}/assessment-scribe/`);

type ShowDockArgs = {
  isActive: boolean;
  caseNumber: string | null;
  pathname: string;
  /** True while the scribe page's live Transcript view is on screen. */
  transcriptViewActive?: boolean;
};

/**
 * The dock renders when a session is live AND the in-panel live view isn't the
 * surface the user is looking at — i.e. either they're OFF the owning case's
 * scribe tab, or they're on the scribe tab but viewing a non-Transcript section
 * (the live ScribeSession view only shows on the Transcript section, ).
 * `transcriptViewActive` is the scribe page's published flag for "the live
 * Transcript view is currently on screen"; it defaults to false so the dock
 * shows whenever we can't confirm the live view is up.
 */
export const shouldShowDock = ({
  isActive,
  caseNumber,
  pathname,
  transcriptViewActive = false,
}: ShowDockArgs): boolean => {
  if (!isActive || !caseNumber) return false;
  if (!isScribeTab(pathname, caseNumber)) return true;
  // On the scribe tab: hide the dock only while the live Transcript view owns
  // the panel; show it when viewing any other section.
  return !transcriptViewActive;
};

type WarnArgs = {
  isActive: boolean;
  recordingCaseNumber: string | null;
  currentPath: string;
  targetPath: string;
};

/**
 * Warn only when a live recording is about to LEAVE its owning case:
 * - switching to a different case, or
 * - navigating to a non-case / case-list page.
 *
 * In-case tab navigation (same case, different tab) is frictionless, and so is
 * returning INTO the owning case from elsewhere.
 */
export const shouldWarnOnNavigation = ({
  isActive,
  recordingCaseNumber,
  targetPath,
}: WarnArgs): boolean => {
  if (!isActive || !recordingCaseNumber) return false;
  return caseNumberFromPath(targetPath) !== recordingCaseNumber;
};
